import type { Recipe } from '../types';

// Chicken pot pie, quick-and-easy style: a creamy chicken-and-vegetable
// filling made in one skillet, topped with stir-and-drop cheddar biscuits.
// No pie crust, no rolling, no chopping raw chicken — the thighs simmer whole
// in the gravy and get shredded with two forks. Same frozen mixed vegetables
// as the hamburger soup, so the two share a cart.

export const chickenPotPieBake: Recipe = {
  slug: 'chicken-pot-pie-bake',
  title: 'Chicken Pot Pie Bake',
  meal: 'dinner',
  category: 'meal',
  types: ['bake', 'casserole'],
  cuisines: ['american'],
  traits: ['easy-prep', 'freezer-friendly', 'make-ahead', 'high-protein'],
  yield: 'one 9x13 pan — 8 servings',
  activeTime: '25 min',
  totalTime: '1 hr',
  freezerLife: '3 months',
  difficulty: 1,

  summary:
    "All the comfort of chicken pot pie with none of the crust work. The filling is made in one big skillet: butter, onion, a creamy gravy, chicken thighs simmered right in it, and two bags of frozen mixed vegetables. Pour it into a 9x13, drop spoonfuls of cheddar biscuit dough on top, bake. One onion is the only thing to chop.",

  flavorNotes:
    "Creamy, savory, buttery — classic chicken pot pie gravy with thyme and black pepper, tender shredded chicken and sweet vegetables, under golden biscuits that are crisp on top and soft underneath where they soak up the gravy. The gravy should be thick enough to coat a spoon, not soupy. If it tastes flat, it needs salt.",

  ingredients: [
    {
      id: 'butter',
      name: 'Unsalted butter',
      total: '12 tbsp (1½ sticks)',
      shop: 'Unsalted butter, 1 lb (4 sticks)',
      uses: [
        { step: 1, amount: '6 tbsp', note: 'for the gravy' },
        { step: 4, amount: '6 tbsp', note: 'melted, for the biscuits' },
      ],
    },
    {
      id: 'onion',
      name: 'Yellow onion',
      total: '1 large',
      prep: 'diced',
      shop: 'Fresh yellow onion, each (1)',
      uses: [{ step: 1, amount: '1 large' }],
    },
    {
      id: 'ap-flour',
      name: 'All-purpose flour',
      total: '⅔ cup + 2 cups',
      uses: [
        { step: 1, amount: '⅔ cup', note: 'thickens the gravy' },
        { step: 4, amount: '2 cups', note: 'biscuits' },
      ],
    },
    {
      id: 'thyme',
      name: 'Dried thyme',
      total: '1 tsp',
      uses: [{ step: 1, amount: '1 tsp' }],
    },
    {
      id: 'garlic-powder',
      name: 'Garlic powder',
      total: '1 tsp',
      uses: [{ step: 1, amount: '1 tsp' }],
    },
    {
      id: 'salt',
      name: 'Salt',
      total: '2 tsp',
      uses: [
        { step: 2, amount: '1 tsp', note: 'gravy, plus more to taste' },
        { step: 4, amount: '1 tsp', note: 'biscuits' },
      ],
    },
    {
      id: 'pepper',
      name: 'Black pepper',
      total: '½ tsp',
      uses: [{ step: 2, amount: '½ tsp' }],
    },
    {
      id: 'broth',
      name: 'Chicken broth',
      total: '4 cups',
      prep: 'Better Than Bouillon chicken base, 1 tsp per cup of hot water',
      uses: [{ step: 2, amount: '4 cups' }],
    },
    {
      id: 'milk',
      name: 'Whole milk',
      total: '2 cups',
      uses: [
        { step: 2, amount: '1 cup', note: 'gravy' },
        { step: 4, amount: '1 cup', note: 'biscuits' },
      ],
    },
    {
      id: 'chicken',
      name: 'Boneless skinless chicken thighs',
      total: '2 lb',
      prep: 'whole — no cutting. They simmer in the gravy and get shredded',
      shop: 'Perdue Harvestland free-range boneless skinless chicken thighs, about 2 lb (size off the tray minimum)',
      uses: [{ step: 2, amount: '2 lb' }],
    },
    {
      id: 'mixed-veg',
      name: 'Frozen mixed vegetables',
      total: '5 cups',
      prep: 'straight from frozen — corn, peas, carrots, green beans',
      shop: 'Great Value frozen mixed vegetables, 12 oz bag (2)',
      uses: [{ step: 3, amount: '5 cups', note: 'two 12 oz bags' }],
    },
    {
      id: 'baking-powder',
      name: 'Baking powder',
      total: '1 tbsp',
      uses: [{ step: 4, amount: '1 tbsp' }],
    },
    {
      id: 'sugar',
      name: 'Sugar',
      total: '1 tbsp',
      uses: [{ step: 4, amount: '1 tbsp' }],
    },
    {
      id: 'cheddar',
      name: 'Tillamook sharp cheddar',
      total: '1 cup',
      prep: 'shredded from the block',
      shop: 'Tillamook sharp cheddar block, 8 oz',
      uses: [{ step: 4, amount: '1 cup', note: '¾ in the dough, ¼ on top' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Butter, onion, flour',
      time: '7 min',
      ingredientIds: ['butter', 'onion', 'ap-flour', 'thyme', 'garlic-powder'],
      body:
        "Heat the oven to 425°F. In a big, deep skillet or Dutch oven over medium, melt {6 tbsp|butter} of butter and cook the diced onion until soft, about 5 minutes. Sprinkle in {⅔ cup|ap-flour} of flour, {1 tsp|thyme} of thyme and {1 tsp|garlic-powder} of garlic powder and stir constantly for 1 minute.",
    },
    {
      n: 2,
      title: 'Make the gravy and simmer the chicken in it',
      time: '18 min',
      critical: true,
      ingredientIds: ['broth', 'milk', 'salt', 'pepper', 'chicken'],
      body:
        "Slowly whisk in {4 cups|broth} of broth, a splash at a time at first so it stays smooth, then {1 cup|milk} of milk, {1 tsp|salt} of salt and {½ tsp|pepper} of pepper. Bring it to a gentle bubble — it will thicken into gravy. Lay the {2 lb|chicken} of whole chicken thighs into the gravy, cover, and simmer on low 15 minutes, until the chicken reaches 175°F. Stir the bottom once or twice so nothing sticks.",
      tip:
        "Cooking the chicken in the gravy means no cutting board for raw chicken and no extra pan. If the gravy gets too thick while it simmers, whisk in a splash of water or broth.",
    },
    {
      n: 3,
      title: 'Shred and add the vegetables',
      time: '5 min',
      ingredientIds: ['mixed-veg'],
      body:
        "Pull the thighs out onto a plate and shred them with two forks right there. Stir the chicken back into the gravy along with {5 cups|mixed-veg} of frozen mixed vegetables. Bring it back to a bubble, taste for salt, and pour it into a 9x13 pan. It must go in hot — hot filling is what cooks the biscuits from underneath.",
    },
    {
      n: 4,
      title: 'Stir-and-drop biscuits on top',
      time: '5 min',
      critical: true,
      ingredientIds: ['ap-flour', 'baking-powder', 'sugar', 'salt', 'cheddar', 'butter', 'milk'],
      body:
        "In a bowl stir {2 cups|ap-flour} of flour, {1 tbsp|baking-powder} of baking powder, {1 tbsp|sugar} of sugar, {1 tsp|salt} of salt and ¾ of the cheddar. Melt {6 tbsp|butter} of butter, pour it and {1 cup|milk} of milk into the bowl, and stir with a spoon just until a sticky dough forms — these are drop biscuits, no rolling. Drop it in about 12 big spoonfuls evenly over the hot filling, leaving small gaps between them. Sprinkle the last of the cheese on top.",
      tip:
        "These are drop biscuits — no rolling, no cutting in cold butter. Lumpy, shaggy dough is right. Leave gaps so steam can escape and the biscuits cook through underneath.",
    },
    {
      n: 5,
      title: 'Bake',
      time: '25 min',
      ingredientIds: [],
      body:
        "Bake 20 to 25 minutes, until the biscuits are deep golden on top and the filling is bubbling at the edges. Lift the edge of one biscuit with a fork: it should be cooked, not doughy, underneath. Let it rest 10 minutes — the gravy thickens as it cools.",
    },
  ],

  freezing:
    "Bake it first, then freeze. Cool completely, then either wrap the whole pan tightly in plastic wrap and foil, or cut into 8 squares and freeze each in a container. Up to 3 months. Use a foil pan if the whole thing is going to the freezer. The biscuits come back softer than fresh — still good.",

  reheat: [
    {
      method: 'Oven',
      best: true,
      detail:
        "Thaw in the fridge overnight. Covered with foil, 350°F: about 35 minutes for the whole pan, 20 minutes for a single square. Uncover for the last 10 minutes so the biscuits crisp back up.",
    },
    {
      method: 'Microwave',
      detail:
        "One thawed square, covered, 2 to 3 minutes. From frozen, 4 to 5 minutes at 50% power, then 1 minute at full. Soft biscuits, fast.",
    },
  ],

  glossary: [
    {
      term: 'gravy',
      plain:
        "Broth and milk thickened with butter and flour into a creamy sauce. The flour has to cook in the butter for a minute first, or the gravy tastes like raw flour.",
    },
    {
      term: 'drop biscuits',
      plain:
        "Biscuits made from a wet dough you spoon on, instead of rolling it out and cutting circles. Less tidy and a lot less work.",
    },
    {
      term: 'simmer',
      plain:
        "Small, gentle bubbles breaking the surface — lower than a full boil. Keeps the chicken tender and the gravy from scorching.",
    },
  ],

  inspiration: [
    { label: 'The Real Food Dietitians — Chicken pot pie casserole', url: 'https://therealfooddietitians.com/chicken-pot-pie-casserole/' },
    { label: 'Barefeet in the Kitchen — Chicken pot pie casserole', url: 'https://barefeetinthekitchen.com/chicken-pot-pie-casserole/' },
  ],

  whereWeVeered:
    "The Real Food Dietitians gave the base: cheddar biscuit topping and frozen mixed vegetables. Barefeet in the Kitchen gave the milk in the gravy. To keep it quick and easy we dropped the celery, simmer the chicken thighs whole in the gravy instead of chopping raw chicken, and use melted butter in the biscuits so nobody has to cut cold butter into flour. Scaled up to 2 lb of chicken and two bags of vegetables for a full 9x13. The filling goes in hot so the biscuits cook through without the pre-bake step.",
};
